import { Controller } from "@hotwired/stimulus";
import {
  templatedEvents,
  setupOutcomeDropdownChoiceManagement,
  filterTemplateDropdownList,
} from "../../../../frontend/javascript/utils/outreach_events";
import { get, post } from '@rails/request.js'

export default class extends Controller {
  static targets = [
    "attachedFileDropzone",
    "callScriptSelect",
    "contactMethodSelect",
    "contactResultSelect",
    "directionSelect",
    "messageBodyWrapper",
    "messageBodyInput",
    "outcomeSelect",
    "referenceSelect",
    "scriptTemplateCanvas",
    "submitButton",
    "templatedEventsExperience",
    "templateUsedSelect"
  ]

  static values = {
    scriptUrl: String,
    templatePreviewUrl: String,
    templateMetadata: Object
  }

  connect() {
    this.contactResultDropdownWrapper = this.element.querySelector(
      "#outreach_event_applicant_report_contact_result_select_wrapper",
    );
    setupOutcomeDropdownChoiceManagement(
      this.outcomeSelectTarget,
      this.contactMethodSelectTarget,
      this.directionSelectTarget,
      this.contactResultSelectTarget,
    );
    this.#setupTemplatedEventsExperience();
  }

  attachedFileDropzoneTargetConnected() {
    this.#setupAttachedFileDropzoneManagement();
  }

  contactMethodSelectTargetConnected() {
    this.handleContactMethodChange();
  }

  directionSelectTargetConnected() {
    this.handleDirectionChange();
  }

  handleContactMethodChange(event) {
    this.#toggleScriptVisibility();
    this.#toggleMessageTemplateVisibility();
    this.#updateScriptOrTemplate();
    this.#toggleContactResultVisibility();
  }

  handleDirectionChange(event) {
    this.dispatch("handleDirectionChange", { detail: { direction: this.directionSelectTarget.value } })
  }

  updateCallScript(event) {
    get(this.scriptUrlValue, {
      query: { script_name: this.callScriptSelectTarget.value },
      responseKind: 'turbo-stream'
    })
  }

  updateTemplatePreview(event) {
    get(this.templatePreviewUrlValue, {
      query: {
        template_name: this.templateUsedSelectTarget.value,
        contact_method: this.contactMethodSelectTarget.value,
        reference: this.referenceSelectTarget.value
      },
      responseKind: 'turbo-stream'
    })
  }

  handleFormSubmit(event) {
    event.preventDefault();

    // Disable the submit button immediately
    this.submitButtonTarget.disabled = true;

    const primaryFormData = new FormData(this.element);
    const secondaryFormData = new FormData(document.querySelector("#new_outreach_event_outreach_eventable_form"));

    for (const [key, value] of secondaryFormData.entries()) {
      primaryFormData.set(key, value);
    }

    post(this.element.action, {
      body: primaryFormData,
      responseKind: 'html'
    }).then(response => {
      // Check if redirects occurred during the request
      if (response.response.redirected) {
        // Navigate to the final URL after redirects
        window.location.href = response.response.url;
      } else {
        // Handle non-redirect responses (like validation errors)
        // Update the page content with the returned HTML
        response.html.then(html => {
          document.documentElement.innerHTML = html;
        });
      }
    }).catch(error => {
      console.error('Form submission error:', error);
      // Re-enable the button on error
      this.submitButtonTarget.disabled = false;
    });
  }

  #toggleContactResultVisibility() {
    const value = this.contactMethodSelectTarget.value !== "CALL";
    this.#toggleElementAndParentVisibility(this.contactResultSelectTarget, value)
  }

  #toggleScriptVisibility() {
    const value = this.contactMethodSelectTarget.value !== "CALL";
    this.#toggleElementAndParentVisibility(this.callScriptSelectTarget, value )
  }

  #toggleMessageTemplateVisibility() {
    const templated_contact_methods = ["EMAIL", "TEXT"];
    const value = !templated_contact_methods.includes(this.contactMethodSelectTarget.value);
    this.#toggleElementAndParentVisibility(this.templateUsedSelectTarget, value)
  }

  #toggleMessageBodyWrapperVisibility() {
    if (this.templateMetadataValue[this.templateUsedSelectTarget.value] && this.templateMetadataValue[this.templateUsedSelectTarget.value].allows_message_body_input) {
      this.messageBodyWrapperTarget.hidden = false;
      this.messageBodyInputTarget.hidden = false;
      this.#toggleElementAndParentVisibility(this.messageBodyInputTarget, false)
      this.messageBodyInputTarget.required = true;
    } else {
      this.messageBodyWrapperTarget.hidden = true;
      this.messageBodyInputTarget.hidden = true;
      this.#toggleElementAndParentVisibility(this.messageBodyInputTarget, true)
      this.messageBodyInputTarget.required = false;
    }
  }

  #updateScriptOrTemplate() {
    if (this.contactMethodSelectTarget.value === "CALL") {
      this.updateCallScript()
    } else if (this.contactMethodSelectTarget.value === "EMAIL" || this.contactMethodSelectTarget.value === "TEXT") {
      this.updateTemplatePreview()
    } else {
      this.scriptTemplateCanvasTarget.innerHTML = "";
    }
  }

  #toggleElementAndParentVisibility(element, value) {
    element.hidden = value;
    element.disabled = value; // excludes input from form submission
    element.parentElement.hidden = value;
  }

  #setupAttachedFileDropzoneManagement() {
    let dropzone = this.attachedFileDropzoneTarget;
    this.outcomeSelectTarget.addEventListener("change", (e) => {
      manageDropzone(e.target.value);
    });

    manageDropzone(this.outcomeSelectTarget.value);

    function manageDropzone(selectedOutcome) {
      if (selectedOutcome == "RECEIVED_LEDGER") {
        showDropzone();
      } else {
        hideDropzone();
      }
    }

    function showDropzone() {
      dropzone.hidden = false;
    }

    function hideDropzone() {
      dropzone.hidden = true;
    }
  }

  #setupTemplatedEventsExperience() {
    const toggleReferencePickerBasedOnTemplate = () => {
      if (
        this.templateMetadataValue[this.templateUsedSelectTarget.value] &&
        this.templateMetadataValue[this.templateUsedSelectTarget.value].requires_reference
      ) {
        this.#toggleElementAndParentVisibility(this.referenceSelectTarget, false)
      } else {
        this.#toggleElementAndParentVisibility(this.referenceSelectTarget, true)
      }
    };

    const toggleTemplatedEventsExperience = () => {
      if (templatedEvents().includes(this.contactMethodSelectTarget.value) && this.directionSelectTarget.value === "OUTBOUND") {
        this.templatedEventsExperienceTarget.hidden = false;
        this.#toggleElementAndParentVisibility(this.templateUsedSelectTarget, false)
      } else {
        this.templatedEventsExperienceTarget.hidden = true;
        this.#toggleElementAndParentVisibility(this.templateUsedSelectTarget, true)
      }
    };

    const setupTemplateDropdownList = () => {
      filterTemplateDropdownList(this.templateMetadataValue, this.contactMethodSelectTarget, this.templateUsedSelectTarget);
    };

    const selectCorrectOutcomeBasedOnTemplateUsed = () => {
      if (
        this.templateMetadataValue[this.templateUsedSelectTarget.value] &&
        this.templateMetadataValue[this.templateUsedSelectTarget.value].expected_outcome &&
        !this.outcomeSelectTarget.value
      ) {
        this.outcomeSelectTarget.value = this.templateMetadataValue[this.templateUsedSelectTarget.value].expected_outcome;
      }
    };

    this.templateUsedSelectTarget.addEventListener("change", (e) => {
      toggleReferencePickerBasedOnTemplate();
      selectCorrectOutcomeBasedOnTemplateUsed();
      this.#toggleMessageBodyWrapperVisibility();
      setupTemplateDropdownList();
    });
    this.contactMethodSelectTarget.addEventListener("change", (e) => {
      toggleTemplatedEventsExperience();
      this.#toggleMessageBodyWrapperVisibility();
      setupTemplateDropdownList();
    });
    this.directionSelectTarget.addEventListener("change", (e) => {
      toggleTemplatedEventsExperience();
    });
    toggleReferencePickerBasedOnTemplate();
    toggleTemplatedEventsExperience();
    selectCorrectOutcomeBasedOnTemplateUsed();
    this.#toggleMessageBodyWrapperVisibility();
    setupTemplateDropdownList();
  }
}
